import classNames from 'classnames';
import { IconChevronDown } from 'featherico';
import PropTypes from 'prop-types';
import { useLayoutEffect, useRef, useState } from 'react';
import { compose, mapProps } from '../../hoc/recompose';
import withConfirmModal from '../../hoc/withConfirmModal';
import withDisabledCheck from '../../hoc/withDisabledCheck';
import withModal from '../../hoc/withModal';
import Button from '../Button';
import withExportModal from '../ExportModal/withExportModal';
import { BEHAVIOR, composeOption, createBaseOption, createDropdown } from '../GenericDropdownFactory';
import Link from '../Link';
import styles from './style.m.less';

export const BaseOption = createBaseOption({ activeClassName: styles.activeOption })(props => (
    <Button kind="LINK" {...props} />
));

export const createLinkOption = component =>
    compose(
        mapProps(props => ({ onClick: () => {}, ...props })),
        composeOption()
    )(createBaseOption({ activeClassName: styles.activeOption })(component));

export const Option = composeOption()(BaseOption);
export const OptionLink = createLinkOption(props => <Link testId="dropdown-item" {...props} />);

export const OptionWithConfirm = composeOption(withConfirmModal)(BaseOption);
export const OptionWithDisabledCheck = composeOption(withDisabledCheck)(BaseOption);
export const OptionWithDisabledCheckWithConfirm = composeOption(withConfirmModal, withDisabledCheck)(BaseOption);
export const OptionWithModal = composeOption(
    mapProps(props => ({
        ...props,
        modalProps: {
            ...props.modalProps,
            onClose: props.onClose,
        },
    })),
    withModal
)(BaseOption);
export const OptionWithModalAndDisabledCheck = composeOption(withModal, withDisabledCheck)(BaseOption);
export const OptionWithExportModal = composeOption(withExportModal)(BaseOption);

export const Dropdown = createDropdown({
    behavior: BEHAVIOR.DROPDOWN,
    wrapperClassName: 'ui-dropdown',
    wrapperOpenClassName: 'hover',
    menuComponent: MenuComponent,
    triggerComponent: ({
        kind = 'DEFAULT',
        style,
        buttonClass,
        noDefaultButtonStyle,
        label,
        ariaLabel,
        onClick,
        onFocus,
        tabIndex,
        'aria-expanded': ariaExpanded,
        showChevron = true,
        eventTracking,
        disabled = false,
    }) => {
        const buttonClassName = classNames(buttonClass, {
            small: !noDefaultButtonStyle,
        });
        return (
            <Button
                kind={kind}
                onFocus={onFocus}
                className={buttonClassName}
                style={style}
                onMouseDown={onClick}
                onTouchEnd={onClick}
                tabIndex={tabIndex}
                aria-haspopup="true"
                aria-expanded={ariaExpanded}
                aria-label={ariaLabel}
                eventTracking={eventTracking}
                disabled={disabled}
                testId="dropdown-open"
            >
                {label} {showChevron && <IconChevronDown className="no-print" />}
            </Button>
        );
    },
});

function MenuComponent({ children }) {
    const ref = useRef();
    const [openUpwards, setOpenUpwards] = useState(false);

    useLayoutEffect(() => {
        if (!ref.current) {
            return;
        }
        const dropRect = ref.current.getBoundingClientRect();
        setOpenUpwards(dropRect.bottom > window.innerHeight);
    }, []);

    return (
        <div className={classNames('ui-dropdown-drop', { 'top-aligned': openUpwards })}>
            <ul ref={ref}>{children}</ul>
        </div>
    );
}

Dropdown.propTypes = {
    label: PropTypes.node,
    ariaLabel: (props, propName, componentName) => {
        if (!props.label && !props.ariaLabel) {
            return new Error(`Please specify a label and/or an ariaLabel for ${componentName}.`);
        }
    },
    eventTracking: PropTypes.shape({
        category: PropTypes.string.isRequired,
        action: PropTypes.string,
    }),
    large: PropTypes.bool,
};

export const Divider = () => <hr className={styles.divider} />;
